import { useState, useEffect } from 'react'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { ArrowLeft, PaperPlaneTilt, Spinner, Users, Buildings } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import api from '../../services/api'

interface FreightForwarder {
  id: string
  name: string
  tradingName: string
  email: string
  tenantName: string
  isPlatformAgent: boolean
  userCount: number
}

export default function InviteAgentPage() {
  const navigate = useNavigate()

  const [freightForwarders, setFreightForwarders] = useState<FreightForwarder[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const [selectedFF, setSelectedFF] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('admin')

  useEffect(() => {
    async function fetchFFs() {
      try {
        const res = await api.get('/master/agents/all')
        setFreightForwarders(res.data.freightForwarders || [])
      } catch (err) {
        console.error('Error fetching freight forwarders:', err)
        toast.error('Erro ao carregar agentes de carga')
      } finally {
        setLoading(false)
      }
    }
    fetchFFs()
  }, [])

  const selectedCompany = freightForwarders.find(ff => ff.id === selectedFF)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (!selectedFF || !email) {
      toast.error('Selecione um agente de carga e informe o email')
      return
    }

    setSubmitting(true)
    try {
      const res = await api.post('/master/agents/invite-user', {
        freightForwarderId: selectedFF,
        email,
        role,
      })

      toast.success(res.data.message || `Convite enviado para ${email}`)
      setEmail('')
    } catch (err: any) {
      const message = err.response?.data?.error || 'Erro ao enviar convite'
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AppLayout>
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                Adicionar Usuário a Agente de Carga
              </h1>
              <p className="text-sm text-gray-500">
                Convide um novo usuário para acessar a conta de um agente existente
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
          {/* FF select */}
          <div className="space-y-2">
            <Label htmlFor="ff">Agente de Carga *</Label>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner size={16} className="animate-spin" />
                Carregando agentes...
              </div>
            ) : (
              <select
                id="ff"
                value={selectedFF}
                onChange={(e) => setSelectedFF(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                required
              >
                <option value="">Selecione um agente de carga</option>
                {freightForwarders.map((ff) => (
                  <option key={ff.id} value={ff.id}>
                    {ff.tradingName || ff.name} — {ff.tenantName} ({ff.userCount} usuário{Number(ff.userCount) !== 1 ? 's' : ''})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Selected FF info */}
          {selectedCompany && (
            <div className="bg-gray-50 rounded-lg p-4 flex items-start gap-3">
              <Buildings size={24} className="text-gray-400 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-gray-900">{selectedCompany.tradingName || selectedCompany.name}</p>
                <p className="text-gray-500">{selectedCompany.tenantName}</p>
                <div className="flex items-center gap-1 mt-1 text-gray-500">
                  <Users size={14} />
                  <span>{selectedCompany.userCount} usuário{Number(selectedCompany.userCount) !== 1 ? 's' : ''} atualmente</span>
                </div>
                {selectedCompany.isPlatformAgent && (
                  <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                    Agente da Plataforma
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email do novo usuário *</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@empresa.com"
              required
            />
            <p className="text-xs text-gray-500">
              O usuário receberá um email com link para aceitar o convite e criar sua conta
            </p>
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label htmlFor="role">Papel na empresa</Label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="admin">Administrador</option>
              <option value="user">Usuário</option>
            </select>
          </div>

          {/* Submit */}
          <div className="flex justify-end pt-4 border-t">
            <Button type="submit" disabled={submitting || !selectedFF || !email}>
              {submitting ? (
                <span className="flex items-center gap-2">
                  <Spinner size={16} className="animate-spin" />
                  Enviando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <PaperPlaneTilt size={18} weight="bold" />
                  Enviar Convite
                </span>
              )}
            </Button>
          </div>
        </form>
      </main>
    </AppLayout>
  )
}
